/* The approved banner owns only its floating phones, screen picker and motion preference. */
(() => {
  if (window.velodromAppHeroReady) return;
  window.velodromAppHeroReady = true;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();
  function mount(root) {
    if (instances.has(root)) return;
    const hero = root.querySelector('[data-vlam-hero]');
    const cinema = root.querySelector('[data-vlam-cinema]');
    const nav = root.querySelector('.vla-nav');
    const header = document.querySelector('.section-header');
    const narrow = matchMedia('(max-width: 749px)');
    nav.setAttribute('data-vla-nav-sync', '');
    const images = [...hero.querySelectorAll('[data-vlam-screen]')];
    const buttons = [...hero.querySelectorAll('[data-vlam-group="hero"]')];
    const cleanup = [];
    const state = { paused: false, visible: true, enabled: true, frame: 0, x: 0, y: 0, tx: 0, ty: 0, token: 0, selected: 0, dead: false, navUntil: 0, navTop: -1 };
    const listen = (target, name, fn, options) => { target.addEventListener(name, fn, options); cleanup.push(() => target.removeEventListener(name, fn, options)); };
    const clamp = n => Math.max(-1, Math.min(1, n));
    async function select(index) {
      if (index === state.selected || !images[index]) return;
      state.selected = index;
      const token = ++state.token;
      const image = images[index];
      if (!image.getAttribute('src')) { image.loading = 'eager'; image.srcset = image.dataset.vlamSrcset; image.src = image.dataset.vlamSrc; }
      try { await image.decode(); } catch { if (token === state.token) state.selected = -1; return; }
      if (state.dead || token !== state.token) return;
      images.forEach((el, i) => { el.classList.toggle('is-active', i === index); el.setAttribute('aria-hidden', String(i !== index)); });
      buttons.forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.vlamSelect) === index)));
    }
    function update(now) {
      state.frame = 0;
      if (state.dead || document.hidden) return;
      // Track the actual visible header, including its hide/reveal transition.
      // Class-based offsets jumped before the header finished moving.
      const gap = narrow.matches ? 8 : 12;
      const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      const navTop = headerBottom + gap;
      if (Math.abs(navTop - state.navTop) > .05) {
        nav.style.setProperty('--vla-nav-top', `${navTop.toFixed(2)}px`);
        state.navTop = navTop;
      }
      if (now < state.navUntil) schedule();
      if (!state.enabled || !state.visible || document.hidden || state.dead) return;
      state.x += (state.tx - state.x) * .18;
      state.y += (state.ty - state.y) * .18;
      cinema.style.setProperty('--pointer-x', state.x.toFixed(3));
      cinema.style.setProperty('--pointer-y', state.y.toFixed(3));
      if (Math.abs(state.tx - state.x) + Math.abs(state.ty - state.y) > .002) schedule();
    }
    function schedule() { if (!state.frame && !state.dead) state.frame = requestAnimationFrame(update); }
    function sync() {
      state.enabled = !state.paused && !reduced.matches;
      root.dataset.motion = state.enabled ? 'on' : 'off';
      hero.querySelectorAll('[data-vlam-toggle]').forEach(button => {
        button.setAttribute('aria-pressed', String(!state.enabled)); button.disabled = reduced.matches;
        button.querySelector('[data-vlam-toggle-label]').textContent = state.enabled ? button.dataset.pause : button.dataset.play;
        button.querySelector('[data-vlam-toggle-icon]').textContent = state.enabled ? 'Ⅱ' : '▷';
      });
      if (!state.enabled) { state.x = state.y = state.tx = state.ty = 0; cinema.style.setProperty('--pointer-x', '0'); cinema.style.setProperty('--pointer-y', '0'); }
      root.dispatchEvent(new CustomEvent('velodrom:motionchange', { detail: { enabled: state.enabled } }));
      schedule();
    }
    listen(hero, 'click', event => {
      const button = event.target instanceof Element ? event.target.closest('[data-vlam-select]') : null;
      if (button) select(Number(button.dataset.vlamSelect));
      if (event.target instanceof Element && event.target.closest('[data-vlam-toggle]')) { state.paused = !state.paused; sync(); }
    });
    listen(cinema, 'pointermove', event => {
      if (event.pointerType !== 'mouse' || !state.enabled) return;
      const rect = cinema.getBoundingClientRect();
      state.tx = clamp((event.clientX - rect.left) / rect.width * 2 - 1);
      state.ty = clamp((event.clientY - rect.top) / rect.height * 2 - 1);
      schedule();
    }, { passive: true });
    listen(cinema, 'pointerleave', () => { state.tx = state.ty = 0; schedule(); });
    listen(reduced, 'change', sync); listen(document, 'visibilitychange', schedule);
    listen(window, 'scroll', schedule, { passive: true });
    listen(window, 'resize', schedule, { passive: true });
    listen(narrow, 'change', schedule);
    if (header) {
      const followHeader = () => { state.navUntil = performance.now() + 220; schedule(); };
      const headerClasses = new MutationObserver(followHeader);
      headerClasses.observe(header, { attributes: true, attributeFilter: ['class'] });
      const headerSize = new ResizeObserver(followHeader);
      headerSize.observe(header);
      cleanup.push(() => { headerClasses.disconnect(); headerSize.disconnect(); });
    }
    const observer = new IntersectionObserver(entries => { state.visible = entries[0].isIntersecting; root.dataset.heroVisible = String(state.visible); if (state.visible) schedule(); });
    observer.observe(hero); cleanup.push(() => observer.disconnect());
    root.classList.add('vlam-ready'); sync();
    instances.set(root, () => { state.dead = true; cancelAnimationFrame(state.frame); cleanup.forEach(fn => fn()); nav.removeAttribute('data-vla-nav-sync'); nav.style.removeProperty('--vla-nav-top'); instances.delete(root); });
  }
  const start = scope => scope.querySelectorAll('[data-vlam-banner]').forEach(mount);
  start(document);
  document.addEventListener('shopify:section:load', event => start(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-vlam-banner]').forEach(root => instances.get(root)?.()));
})();
